import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Bar, BarChart, CartesianGrid,
         LabelList, Tooltip, XAxis, YAxis } from "recharts";
import { ArrowLeft, CaretDown, CaretRight, ChartPie, CloudArrowDown,
         DownloadSimple } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { FpShell } from "@/pages/panchatanthra/fp/FpShell";
import { EmptyState } from "@/components/shared";
import { fpBase, inr, monthLabel, useFpContext } from "@/lib/fp";
import { toast } from "sonner";

const PALETTE = ["#c0563a", "#1f3355", "#d99a2b", "#4a7c6f", "#8c4a6b", "#3f7ca6",
                 "#b8863b", "#5d6f8c", "#a4553f", "#2f6b5c", "#7a5aa1", "#c47a3a"];
const colour = (i) => PALETTE[i % PALETTE.length];
const short = (v) => {
  const a = Math.abs(v);
  if (a >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
  if (a >= 1e5) return `₹${(v / 1e5).toFixed(1)}L`;
  if (a >= 1e3) return `₹${Math.round(v / 1e3)}k`;
  return `₹${Math.round(v)}`;
};

/** Headline placards read in words — "₹2.48 crore" — with the exact figure beneath. */
const shortInr = (v) => {
  if (v === null || v === undefined) return "—";
  const a = Math.abs(v);
  const sign = v < 0 ? "−" : "";
  if (a >= 1e7) return `${sign}₹${(a / 1e7).toFixed(2)} crore`;
  if (a >= 1e5) return `${sign}₹${(a / 1e5).toFixed(2)} lakh`;
  if (a >= 1e3) return `${sign}₹${(a / 1e3).toFixed(1)}k`;
  return `${sign}₹${Math.round(a)}`;
};

/** What each level means in the client's own words. */
const LEVEL_COPY = {
  1: { title: "Level 1 — the report totals",
       note: "Revenue, production cost, distribution cost and fixed cost as single figures. "
             + "Nothing splits further at this level." },
  2: { title: "Level 2 — one dimension deep",
       note: "Each report split once: revenue and distribution cost by channel, production cost "
             + "by product category, fixed cost by business function." },
  3: { title: "Level 3 — the full grid",
       note: "Each report split twice: channel then product category, category then cost "
             + "component, function then ledger — right down to the individual line item." },
};

/** A six-month sparkline. The last point turns red when it moves more than 20% against the
 *  previous month — a spike is visible the moment it happens. */
const Spark = ({ points, periods, i, w = 72, h = 22, spikeLabel = false, testid }) => {
  const vals = (points || []).filter((v) => v !== null && v !== undefined);
  if (vals.length < 2) return null;
  const min = Math.min(...vals), max = Math.max(...vals), span = max - min || 1;
  const step = w / ((points.length - 1) || 1);
  const xy = points.map((v, idx) => (v === null || v === undefined)
    ? null : [idx * step, h - 3 - ((v - min) / span) * (h - 6)]);
  const line = xy.filter(Boolean).map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const lastV = vals[vals.length - 1], prevV = vals[vals.length - 2];
  const spike = prevV ? Math.abs(lastV - prevV) / Math.abs(prevV) > 0.2 : false;
  const lastPt = [...xy].reverse().find(Boolean);
  const stroke = spike ? "hsl(0 62% 45%)" : colour(i);
  const tip = (periods || []).map((p, idx) =>
    `${monthLabel(p)} ${points[idx] === null || points[idx] === undefined ? "—" : short(points[idx])}`
  ).join(" · ");
  return (
    <span className="inline-flex items-center gap-1.5" data-testid={testid} title={tip}>
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="shrink-0">
        <polyline points={line} fill="none" stroke={stroke} strokeWidth="1.6"
                  strokeLinejoin="round" strokeLinecap="round" opacity="0.9" />
        {lastPt && <circle cx={lastPt[0]} cy={lastPt[1]} r={spike ? 3 : 2} fill={stroke} />}
      </svg>
      {spikeLabel && spike && (
        <span className="rounded-full bg-destructive/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] text-destructive">
          spike vs last month
        </span>
      )}
    </span>
  );
};

const Delta = ({ node, cost }) => {
  if (node.variance_pp !== null && node.variance_pp !== undefined) {
    const good = node.variance_pp <= 0;
    return (
      <span data-testid={`fp-explorer-delta-pp-${node.id}`}
            className={`rounded-full px-2 py-0.5 text-[10px] font-bold tabular-nums ${
              good ? "bg-success/18 text-success" : "bg-destructive/15 text-destructive"}`}>
        {node.variance_pp > 0 ? "+" : ""}{node.variance_pp} pp vs budget
      </span>
    );
  }
  if (node.variance_pct === null || node.variance_pct === undefined) return null;
  const good = cost ? node.variance <= 0 : node.variance >= 0;
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold tabular-nums ${
      good ? "bg-success/18 text-success" : "bg-destructive/15 text-destructive"}`}>
      {node.variance_pct > 0 ? "+" : ""}{node.variance_pct}% vs budget
    </span>
  );
};

/** One headline placard. The hero (revenue) is a full-width band with the figure in words and the
 *  exact rupee amount beneath; the rest are equal cards. Nothing ever wraps or clips. */
const Placard = ({ h, i, hero = false, active, onPick, pair, trend, periods }) => {
  const pp = h.variance_pp;
  const hasPp = pp !== null && pp !== undefined;
  const variance = hasPp ? null
    : h.budget ? Math.round((h.actual - h.budget) / h.budget * 1000) / 10 : null;
  const good = h.cost ? (hasPp ? pp <= 0 : h.actual <= h.budget) : h.actual >= h.budget;
  return (
    <button disabled={!h.drillable} onClick={onPick} data-testid={`fp-explorer-head-${h.key}`}
            className={`group relative flex min-w-0 overflow-hidden border text-left transition-all ${
              hero ? "w-full flex-col gap-4 rounded-[28px] p-6 sm:flex-row sm:items-end sm:justify-between sm:p-7"
                   : "flex-col justify-between gap-4 rounded-2xl p-5"} ${
              h.drillable
                ? "border-border bg-card hover:-translate-y-0.5 hover:border-terracotta hover:shadow-lg"
                : "border-transparent bg-muted/40"} ${
              active ? "border-terracotta ring-1 ring-terracotta/30" : ""}`}>
      <span aria-hidden className="absolute inset-x-0 top-0 h-[3px]"
            style={{ background: colour(i), opacity: h.drillable ? 0.9 : 0.35 }} />
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: colour(i) }} />
          <p className={`label-caps ${hero ? "truncate" : "leading-snug"}`}>{h.label}</p>
        </div>
        <p className={`mt-3 whitespace-nowrap font-display tabular-nums leading-none text-navy ${
          hero ? "text-[2rem] sm:text-[2.75rem]" : "text-[1.6rem]"}`}
           data-testid={`fp-explorer-head-short-${h.key}`}>
          {shortInr(h.actual)}
        </p>
        <p className="mt-2 whitespace-nowrap font-mono text-[11px] tabular-nums text-muted-foreground"
           data-testid={`fp-explorer-head-full-${h.key}`}>
          {inr(h.actual)}
        </p>
        {h.actual_pct_of_sales !== null && h.actual_pct_of_sales !== undefined && (
          <p className="mt-1 whitespace-nowrap font-mono text-[10px] tabular-nums text-muted-foreground"
             data-testid={`fp-explorer-head-pct-${h.key}`}>
            {h.actual_pct_of_sales}% of sales
            {h.budget_pct_of_sales !== null && h.budget_pct_of_sales !== undefined
              ? ` · budget ${h.budget_pct_of_sales}%` : ""}
          </p>
        )}
        {pair && (
          <p className="mt-1.5 text-[10px] leading-relaxed text-muted-foreground/80 opacity-0 transition-opacity group-hover:opacity-100"
             data-testid={`fp-explorer-head-pair-${h.key}`}
             title={[pair.control_documents?.length ? `Tally: ${pair.control_documents.join(", ")}` : "",
                     pair.detail_documents?.length ? `Detail: ${pair.detail_documents.join(", ")}` : ""]
                     .filter(Boolean).join(" · ")}>
            Tally says {pair.control_total === null ? "no control total" : inr(pair.control_total)}
            {" · detail says "}
            {pair.detail_total === null ? "none" : inr(pair.detail_total)}
          </p>
        )}
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        {trend && <Spark points={trend} periods={periods} i={i}
                         testid={`fp-explorer-spark-${h.key}`} />}
        {hasPp ? (
          <span data-testid={`fp-explorer-head-pp-${h.key}`}
                className={`rounded-full px-2.5 py-1 text-[11px] font-semibold tabular-nums ${
                  good ? "bg-success/15 text-success" : "bg-destructive/12 text-destructive"}`}>
            {pp > 0 ? "+" : ""}{pp} pp vs budget
          </span>
        ) : variance !== null && (
          <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold tabular-nums ${
            good ? "bg-success/15 text-success" : "bg-destructive/12 text-destructive"}`}>
            {variance > 0 ? "+" : ""}{variance}% vs budget
          </span>
        )}
        {h.drillable && (
          <span className="text-[10px] uppercase tracking-[0.12em] text-terracotta opacity-0 transition-opacity group-hover:opacity-100">
            zoom in
          </span>
        )}
      </div>
    </button>
  );
};

const ChartTip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-xl border border-border bg-card/95 px-4 py-3 shadow-lg backdrop-blur">
      <p className="text-xs font-semibold text-navy">{d.label}</p>
      <p className="mt-1 font-mono text-xs text-navy">{inr(d.actual)}</p>
      {d.budget ? (
        <p className="font-mono text-[11px] text-muted-foreground">budget {inr(d.budget)}</p>
      ) : null}
      {d.actual_pct_of_sales !== null && d.actual_pct_of_sales !== undefined && (
        <p className="font-mono text-[11px] text-muted-foreground"
           data-testid={`fp-explorer-tip-pct-${d.id}`}>
          {d.actual_pct_of_sales}% of {d.sales_base ? `${d.sales_base} sales` : "sales"}
          {d.budget_pct_of_sales !== null && d.budget_pct_of_sales !== undefined
            ? ` · budget ${d.budget_pct_of_sales}%` : ""}
        </p>
      )}
      {d.share_pct !== null && d.share_pct !== undefined && (
        <p className="text-[11px] text-muted-foreground">{d.share_pct}% of this level</p>
      )}
      <p className="mt-1 text-[10px] uppercase tracking-[0.1em] text-terracotta">
        click to zoom in
      </p>
    </div>
  );
};

/** Share of total for the current level. Click a slice to go deeper. Negative rows (discounts and
 *  credits) cannot be drawn as a slice, so they are listed beside the chart instead. */
const ShareChart = ({ rows, onPick }) => {
  const positive = rows.filter((r) => r.actual > 0);
  const negative = rows.filter((r) => r.actual <= 0 && r.actual !== 0);
  return (
    <div>
      <ResponsiveContainer width="100%" height={320}>
        <PieChart>
          <Pie data={positive} dataKey="actual" nameKey="label" innerRadius="52%"
               outerRadius="80%" paddingAngle={2} isAnimationActive cursor="pointer"
               onClick={(d) => onPick(d?.payload?.payload || d?.payload)}>
            {positive.map((r, i) => <Cell key={`${r.id}-${i}`} fill={colour(i)} stroke="none" />)}
          </Pie>
          <Tooltip content={<ChartTip />} />
          <Legend verticalAlign="bottom" height={60} iconType="circle"
                  onClick={(d) => onPick(positive.find((r) => r.label === d.value))}
                  wrapperStyle={{ fontSize: 11, cursor: "pointer" }} />
        </PieChart>
      </ResponsiveContainer>
      {negative.length > 0 && (
        <div className="flex flex-wrap gap-2 px-4 pb-2" data-testid="fp-explorer-deductions">
          {negative.map((r) => (
            <button key={r.id} onClick={() => onPick(r)}
                    className="rounded-full border border-border px-3 py-1 text-[11px] text-navy hover:border-terracotta">
              {r.label}: {inr(r.actual)} <span className="text-muted-foreground">deduction</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/** Budget against actual for the current level. Click a bar to go deeper. */
const CompareChart = ({ rows, onPick }) => (
  <ResponsiveContainer width="100%" height={Math.max(280, rows.length * 42)}>
    <BarChart data={rows} layout="vertical" margin={{ left: 8, right: 46, top: 8, bottom: 8 }}>
      <CartesianGrid horizontal={false} stroke="hsl(35 20% 88%)" />
      <XAxis type="number" tickFormatter={short} tick={{ fontSize: 10 }} axisLine={false} />
      <YAxis type="category" dataKey="label" width={148} tick={{ fontSize: 11 }}
             axisLine={false} tickLine={false} />
      <Tooltip content={<ChartTip />} cursor={{ fill: "hsl(35 30% 94%)" }} />
      <Bar dataKey="budget" fill="hsl(215 25% 82%)" radius={[0, 4, 4, 0]} barSize={9}
           name="Budget" />
      <Bar dataKey="actual" radius={[0, 4, 4, 0]} barSize={13} name="Actual" cursor="pointer"
           onClick={(d) => onPick(d?.payload)}>
        {rows.map((r, i) => <Cell key={`${r.id}-${i}`} fill={colour(i)} />)}
        <LabelList dataKey="actual" position="right" formatter={short}
                   style={{ fontSize: 10, fill: "hsl(215 37% 22%)" }} />
      </Bar>
    </BarChart>
  </ResponsiveContainer>
);

const Numbers = ({ rows, cost, onPick }) => (
  <table className="w-full text-xs" data-testid="fp-explorer-numbers">
    <thead className="bg-muted/40 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
      <tr>
        <th className="px-5 py-2.5 text-left">Particulars</th>
        <th className="px-5 py-2.5 text-right">Actual</th>
        <th className="px-5 py-2.5 text-right">Budget</th>
        <th className="px-5 py-2.5 text-right">Variance</th>
        <th className="px-5 py-2.5 text-right">Share</th>
      </tr>
    </thead>
    <tbody className="divide-y divide-border">
      {rows.map((r, i) => (
        <tr key={`${r.id}-${i}`} onClick={() => onPick(r)}
            className="cursor-pointer transition-colors hover:bg-muted/40"
            data-testid={`fp-explorer-row-${r.id}`}>
          <td className="px-5 py-2 text-navy">
            <span className="mr-2 inline-block h-2.5 w-2.5 rounded-full align-middle"
                  style={{ background: colour(i) }} />
            {r.label}
          </td>
          <td className="px-5 py-2 text-right font-mono tabular-nums text-navy">
            {inr(r.actual)}
            {r.actual_pct_of_sales !== null && r.actual_pct_of_sales !== undefined && (
              <span className="block text-[10px] text-muted-foreground"
                    data-testid={`fp-explorer-row-pct-${r.id}`}>
                {r.actual_pct_of_sales}% of {r.sales_base ? `${r.sales_base} sales` : "sales"}
              </span>
            )}
          </td>
          <td className="px-5 py-2 text-right font-mono tabular-nums text-muted-foreground">
            {inr(r.budget)}
            {r.budget_pct_of_sales !== null && r.budget_pct_of_sales !== undefined && (
              <span className="block text-[10px] text-muted-foreground/70"
                    data-testid={`fp-explorer-row-bpct-${r.id}`}>
                {r.budget_pct_of_sales}% of {r.sales_base ? `${r.sales_base} sales` : "sales"}
              </span>
            )}
          </td>
          <td className={`px-5 py-2 text-right font-mono tabular-nums ${
            (cost ? r.variance <= 0 : r.variance >= 0) ? "text-success" : "text-destructive"}`}>
            {r.variance_pp !== null && r.variance_pp !== undefined
              ? `${r.variance_pp > 0 ? "+" : ""}${r.variance_pp} pp`
              : r.variance_pct === null ? "—" : `${r.variance_pct > 0 ? "+" : ""}${r.variance_pct}%`}
          </td>
          <td className="px-5 py-2 text-right font-mono tabular-nums text-navy">
            {r.share_pct === null ? "—" : `${r.share_pct}%`}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

/** DVC pivoted the other way: every cost component summed across all outlets, with the
 *  outlet split nested beneath. Click a pie slice or a table row to open its outlets. */
const DvcByComponent = ({ tree, trends, periods }) => {
  const [openId, setOpenId] = useState(null);
  const rows = tree?.children || [];
  if (!rows.length) return null;
  const toggle = (row) => row?.id && setOpenId(row.id === openId ? null : row.id);
  return (
    <div className="panel overflow-hidden" data-testid="fp-explorer-dvc-by-component">
      <div className="border-b border-border px-6 py-4">
        <p className="label-caps">Distribution variable cost · all outlets</p>
        <h3 className="font-display text-lg text-navy">
          DVC by component — {inr(tree.actual)} across every outlet
        </h3>
        <p className="mt-1 text-[11px] text-muted-foreground">
          The same distribution cost, grouped by component instead of by outlet. Click a
          component to see which outlets drive it.
        </p>
      </div>
      <div className="grid grid-cols-1 gap-2 p-4 lg:grid-cols-2">
        <ShareChart rows={rows} onPick={toggle} />
        <div className="min-w-0 self-start overflow-hidden rounded-xl border border-border">
          <table className="w-full text-xs" data-testid="fp-explorer-dvc-comp-table">
            <thead className="bg-muted/40 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
              <tr>
                <th className="px-4 py-2.5 text-left">Component</th>
                <th className="px-4 py-2.5 text-right">Actual</th>
                <th className="px-4 py-2.5 text-right">Share</th>
                <th className="px-4 py-2.5 text-right">6 months</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r, i) => (
                <Fragment key={r.id}>
                  <tr onClick={() => toggle(r)}
                      className={`cursor-pointer transition-colors hover:bg-muted/40 ${openId === r.id ? "bg-muted/30" : ""}`}
                      data-testid={`fp-explorer-dvc-comp-${r.id}`}>
                    <td className="px-4 py-2 text-navy">
                      <span className="mr-2 inline-block h-2.5 w-2.5 rounded-full align-middle"
                            style={{ background: colour(i) }} />
                      {r.label}
                      <span className="ml-2 inline-block align-middle text-muted-foreground">
                        {openId === r.id ? <CaretDown size={12} /> : <CaretRight size={12} />}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right font-mono tabular-nums text-navy">
                      {inr(r.actual)}
                    </td>
                    <td className="px-4 py-2 text-right font-mono tabular-nums text-navy">
                      {r.share_pct === null ? "—" : `${r.share_pct}%`}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Spark points={trends?.nodes?.[r.id]} periods={periods} i={i}
                             testid={`fp-explorer-dvc-comp-spark-${r.id}`} />
                    </td>
                  </tr>
                  {openId === r.id && (r.children || []).map((c) => (
                    <tr key={c.id} className="bg-muted/20"
                        data-testid={`fp-explorer-dvc-comp-outlet-${c.id}`}>
                      <td className="px-4 py-1.5 pl-9 text-[11px] text-muted-foreground">
                        {c.label}
                      </td>
                      <td className="px-4 py-1.5 text-right font-mono text-[11px] tabular-nums text-muted-foreground">
                        {inr(c.actual)}
                      </td>
                      <td className="px-4 py-1.5 text-right font-mono text-[11px] tabular-nums text-muted-foreground">
                        {c.share_pct === null ? "—" : `${c.share_pct}%`}
                      </td>
                      <td className="px-4 py-1.5 text-right">
                        <Spark points={trends?.nodes?.[c.id]} periods={periods} i={i}
                               w={56} h={18}
                               testid={`fp-explorer-dvc-comp-outlet-spark-${c.id}`} />
                      </td>
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="border-t border-border px-6 py-3 text-[11px] text-muted-foreground">
        Adds up to the same {inr(tree.actual)} as the outlet-first DVC view — only the grouping
        changes.
      </p>
    </div>
  );
};

/** Cost object lifecycle: month-to-date, inception-to-date and the WIP an open object carries. */
const CostObjects = ({ tenantId, period }) => {
  const [data, setData] = useState(null);
  const [scope, setScope] = useState("itd");
  const [filter, setFilter] = useState("all");
  const [openRow, setOpenRow] = useState(null);

  const load = useCallback(async () => {
    if (!tenantId || !period) return;
    try {
      const { data: out } = await api.get(`${fpBase(tenantId)}/cost-objects`, { params: { period } });
      setData(out);
    } catch { setData(null); }
  }, [tenantId, period]);

  useEffect(() => { load(); }, [load]);

  const flip = async (row) => {
    try {
      await api.put(`${fpBase(tenantId)}/cost-objects/${encodeURIComponent(row.label)}/status`,
                    { status: row.closed ? "open" : "closed" });
      await load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!data?.is_cost_object || !(data.rows || []).length) return null;
  const rows = (data.rows || []).filter((r) =>
    filter === "all" ? true : filter === "open" ? !r.closed : r.closed);
  const itd = scope === "itd";
  const chartRows = rows.map((r) => ({ label: r.label,
                                       actual: itd ? r.itd_cost : r.mtd_cost }));

  return (
    <div className="panel overflow-hidden" data-testid="fp-explorer-cost-objects">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-6 py-4">
        <div className="min-w-0">
          <p className="label-caps">Production variable cost · by {data.object_label.toLowerCase()}</p>
          <h3 className="font-display text-lg text-navy">
            {data.totals.objects} {data.object_label.toLowerCase()}
            {data.totals.objects === 1 ? "" : "s"} — {data.totals.open} still open,{" "}
            {inr(itd ? data.totals.itd_cost : data.totals.mtd_cost)} of cost
          </h3>
          <p className="mt-1 max-w-2xl text-[11px] text-muted-foreground">
            {data.wip_mode_label}. {data.note}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api${fpBase(tenantId)}/cost-objects.xlsx?period=${period}`}
             data-testid="fp-cost-object-download"
             className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-[10px] font-bold uppercase tracking-[0.12em] text-navy hover:border-terracotta">
            <DownloadSimple size={13} /> WIP schedule
          </a>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api${fpBase(tenantId)}/cost-objects/template.xlsx?period=${period}`}
             data-testid="fp-cost-object-template"
             className="inline-flex items-center gap-2 rounded-full border border-dashed border-border px-4 py-2 text-[10px] font-bold uppercase tracking-[0.12em] text-navy hover:border-terracotta">
            <DownloadSimple size={13} /> Upload template
          </a>
          <div className="flex overflow-hidden rounded-full border border-border">
            {[["mtd", "This month"], ["itd", `${data.fiscal_year} to date`]].map(([k, l]) => (
              <button key={k} onClick={() => setScope(k)} data-testid={`fp-cost-object-scope-${k}`}
                      className={`px-4 py-2 text-[10px] font-bold uppercase tracking-[0.12em] ${
                        scope === k ? "bg-navy text-white" : "bg-card text-navy hover:bg-muted"}`}>
                {l}
              </button>
            ))}
          </div>
          <div className="flex overflow-hidden rounded-full border border-border">
            {[["all", "All"], ["open", "Open"], ["closed", "Closed"]].map(([k, l]) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`fp-cost-object-filter-${k}`}
                      className={`px-4 py-2 text-[10px] font-bold uppercase tracking-[0.12em] ${
                        filter === k ? "bg-terracotta text-white" : "bg-card text-navy hover:bg-muted"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2 p-4 lg:grid-cols-2">
        <ShareChart rows={chartRows} onPick={() => {}} />
        <div className="min-w-0 self-start overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-xs" data-testid="fp-cost-object-table">
            <thead className="bg-muted/40 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
              <tr>
                <th className="px-3 py-2.5 text-left">{data.object_label}</th>
                <th className="px-3 py-2.5 text-right">Cost</th>
                <th className="px-3 py-2.5 text-right">Revenue</th>
                <th className="px-3 py-2.5 text-right">WIP carried</th>
                <th className="px-3 py-2.5 text-left">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r, i) => (
                <Fragment key={r.label}>
                  <tr onClick={() => setOpenRow(openRow === r.label ? null : r.label)}
                      className="cursor-pointer transition-colors hover:bg-muted/40"
                      data-testid={`fp-cost-object-row-${r.label}`}>
                    <td className="px-3 py-2 text-navy">
                      <span className="mr-2 inline-block h-2.5 w-2.5 rounded-full align-middle"
                            style={{ background: colour(i) }} />
                      {r.label}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-navy">
                      {inr(itd ? r.itd_cost : r.mtd_cost)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-navy">
                      {inr(itd ? r.itd_revenue : r.mtd_revenue)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-navy">
                      {r.wip_carried ? inr(r.wip_carried) : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <button onClick={(e) => { e.stopPropagation(); flip(r); }}
                              data-testid={`fp-cost-object-status-${r.label}`}
                              className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.1em] ${
                                r.closed ? "bg-muted text-navy" : "bg-warning/20 text-[hsl(30_88%_28%)]"}`}>
                        {r.status}
                      </button>
                    </td>
                  </tr>
                  {openRow === r.label && (
                    <tr className="bg-muted/20" data-testid={`fp-cost-object-detail-${r.label}`}>
                      <td colSpan={5} className="px-3 py-3">
                        <p className="text-[11px] text-muted-foreground">
                          First charged {monthLabel(r.first_period)} · last{" "}
                          {monthLabel(r.last_period)} · inception-to-date margin{" "}
                          {inr(r.itd_margin)}
                          {r.itd_margin_pct === null ? "" : ` (${r.itd_margin_pct}%)`}
                        </p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {(r.components || []).map((c) => (
                            <span key={c.label}
                                  className="rounded-full bg-card px-3 py-1 text-[11px] text-navy">
                              {c.label} · {inr(c.amount)}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="border-t border-border px-6 py-3 text-[11px] text-muted-foreground">
        Engine-carried WIP on open {data.object_label.toLowerCase()}s:{" "}
        {inr(data.totals.engine_wip)}
        {data.wip_mode === "tally_ledger"
          ? ` · Tally ${data.wip_ledger} closing balance: ${inr(data.totals.tally_wip)}`
          : ""}. Click a row for its components; click the status to override it.
      </p>
    </div>
  );
};

const Sources = ({ tenantId, period, onSynced }) => {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data: out } = await api.get(`${fpBase(tenantId)}/sheet-sources`);
    setData(out);
    setDraft(Object.fromEntries(out.rows.map((r) => [r.key, { ...r }])));
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await api.put(`${fpBase(tenantId)}/sheet-sources`, { items: Object.values(draft) });
      toast.success("Report links saved");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const sync = async () => {
    setBusy(true);
    try {
      const { data: out } = await api.post(`${fpBase(tenantId)}/sheet-sources/sync`, null,
                                           { params: { period } });
      out.results.forEach((r) => (r.status === "ok"
        ? toast.success(`${r.key}: pulled ${inr(r.total)}`)
        : toast.warning(`${r.key}: ${r.status}`)));
      onSynced();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); load(); }
  };

  if (!data?.can_configure) return null;
  const set = (key, patch) => setDraft({ ...draft, [key]: { ...draft[key], ...patch } });

  return (
    <div className="panel overflow-hidden" data-testid="fp-explorer-sources">
      <button onClick={() => setOpen(!open)} data-testid="fp-explorer-sources-toggle"
              className="flex w-full items-center justify-between gap-3 px-6 py-4 text-left hover:bg-muted/30">
        <div>
          <p className="label-caps">Report sheets</p>
          <h3 className="font-display text-lg text-navy">
            Where the explorer reads its figures from
          </h3>
        </div>
        {open ? <CaretDown size={18} /> : <CaretRight size={18} />}
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.22 }}
                      className="overflow-hidden border-t border-border">
            <div className="space-y-3 px-6 py-5">
              {Object.values(draft).map((r) => (
                <div key={r.key} className="grid grid-cols-1 gap-2 md:grid-cols-12"
                     data-testid={`fp-source-${r.key}`}>
                  <div className="md:col-span-3">
                    <p className="text-sm font-medium text-navy">{r.label || r.key}</p>
                    <label className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
                      <input type="checkbox" checked={!!r.enabled}
                             data-testid={`fp-source-enabled-${r.key}`}
                             onChange={(e) => set(r.key, { enabled: e.target.checked })}
                             className="h-3.5 w-3.5 accent-[hsl(12_62%_47%)]" />
                      sync this one
                    </label>
                  </div>
                  <input value={r.link || ""} placeholder="Google Sheet share link"
                         data-testid={`fp-source-link-${r.key}`}
                         onChange={(e) => set(r.key, { link: e.target.value })}
                         className="rounded-lg border border-border bg-card px-3 py-2 text-xs text-navy outline-none focus:border-terracotta md:col-span-6" />
                  <input value={r.tab || ""} placeholder="tab (optional)"
                         data-testid={`fp-source-tab-${r.key}`}
                         onChange={(e) => set(r.key, { tab: e.target.value })}
                         className="rounded-lg border border-border bg-card px-3 py-2 text-xs text-navy outline-none focus:border-terracotta md:col-span-3" />
                </div>
              ))}
              <div className="flex flex-wrap items-center gap-3 pt-2">
                <button onClick={save} data-testid="fp-sources-save"
                        className="rounded-full bg-terracotta px-5 py-2.5 text-[10px] uppercase tracking-[0.14em] text-white hover:bg-terracotta/90">
                  Save links
                </button>
                <button onClick={sync} disabled={busy} data-testid="fp-sources-sync"
                        className="flex items-center gap-2 rounded-full border border-navy px-5 py-2.5 text-[10px] uppercase tracking-[0.14em] text-navy hover:bg-navy hover:text-white disabled:opacity-50">
                  <CloudArrowDown size={14} />{busy ? "Syncing" : "Sync now"}
                </button>
                <button onClick={() => {
                  const key = window.prompt("Name this provision (e.g. capex-register)");
                  if (key) set(key, { key, label: key, kind: "balance_sheet", enabled: true });
                }} data-testid="fp-sources-add"
                        className="text-[10px] uppercase tracking-[0.14em] text-terracotta hover:underline">
                  + Add another provision
                </button>
                <p className="text-[11px] text-muted-foreground">
                  Sheets also refresh on their own every morning at 6.30 IST. Each link must stay
                  shared as “anyone with the link can view”.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function FpExplorer() {
  const { tenantId, period, basis } = useFpContext();
  const [data, setData] = useState(null);
  const [path, setPath] = useState([]);
  const [view, setView] = useState("share");
  const [lens, setLens] = useState("revenue");
  const [showNumbers, setShowNumbers] = useState(true);
  const [level, setLevel] = useState(3);

  const load = useCallback(async () => {
    if (!tenantId || !period) return;
    try {
      const { data: out } = await api.get(`${fpBase(tenantId)}/explorer`,
                                          { params: { period, basis, level } });
      setData(out);
    } catch (e) { setData({ error: errMsg(e) }); }
  }, [tenantId, period, basis, level]);

  useEffect(() => { setPath([]); }, [level]);

  useEffect(() => { load(); }, [load]);

  const { node, crumbs } = useMemo(() => {
    if (!data?.trees || !path.length) return { node: null, crumbs: [] };
    let current = data.trees[path[0]];
    const trail = [{ id: path[0], label: current?.label || path[0] }];
    for (const step of path.slice(1)) {
      current = (current?.children || []).find((c) => c.id === step) || current;
      trail.push({ id: step, label: current?.label || step });
    }
    return { node: current, crumbs: trail };
  }, [data, path]);

  const cost = path[0] !== "revenue";
  const rows = node?.children || [];
  const pick = (row) => row?.id && setPath([...path, row.id]);

  return (
    <FpShell
      title="Explorer"
      description="One chart at a time, and every click zooms in a level: revenue and cost by outlet, then by product category, then by cost component, then down to the individual line item — with budget, actual and deviation at every level."
      actions={period && !data?.error && (
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api${fpBase(tenantId)}/explorer.xlsx?period=${period}&basis=${basis}&level=${level}`}
           data-testid="fp-explorer-download"
           className="inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-2.5 text-[10px] uppercase tracking-[0.14em] text-white hover:bg-terracotta/90">
          <DownloadSimple size={13} /> Download explorer
        </a>
      )}
    >
      {!period ? (
        <EmptyState testid="fp-explorer-empty" title="Nothing uploaded yet"
                    message="Upload a Profit & Loss under Statements first." />
      ) : data?.error ? (
        <EmptyState testid="fp-explorer-error" title="Unavailable" message={data.error} />
      ) : !data ? (
        <p className="label-caps animate-pulse">Building the explorer</p>
      ) : !data.has_data ? (
        <EmptyState testid="fp-explorer-nodata" title={`Nothing loaded for ${monthLabel(period)}`}
                    message={`This engagement has no books uploaded and no report sheet synced for ${monthLabel(period)}, so there is nothing to explore. ${
                      (data.periods_with_data || []).length
                        ? `Months that do have figures: ${(data.periods_with_data || []).map(monthLabel).join(", ")}. Pick one from the Month selector above.`
                        : "Upload the Profit & Loss under Statements, or connect the report sheets, to get started."}`} />
      ) : (
        <div className="space-y-6" data-testid="fp-explorer">
          {/* ---------- which level the drill is built at ---------- */}
          <div className="panel flex flex-wrap items-center justify-between gap-4 p-5"
               data-testid="fp-explorer-levels">
            <div className="min-w-0">
              <p className="label-caps">How deep</p>
              <h3 className="font-display text-lg text-navy">{LEVEL_COPY[level].title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{LEVEL_COPY[level].note}</p>
            </div>
            <div className="flex overflow-hidden rounded-full border border-border">
              {[1, 2, 3].map((l) => (
                <button key={l} onClick={() => setLevel(l)} data-testid={`fp-explorer-level-${l}`}
                        className={`px-5 py-2 text-[10px] font-bold uppercase tracking-[0.12em] ${
                          level === l ? "bg-navy text-white" : "bg-card text-navy hover:bg-muted"}`}>
                  Level {l}
                </button>
              ))}
            </div>
          </div>

          {Object.keys(data.untagged || {}).length > 0 && (
            <div className="panel bg-warning/10 p-5" data-testid="fp-explorer-untagged">
              <p className="label-caps text-[hsl(30_88%_28%)]">Still to be tagged</p>
              <p className="mt-1 text-sm text-navy">
                Some ledgers carry no dimension tag, so they cannot sit inside a Level{" "}
                {level} grid. They are shown as their own “Untagged” block, which keeps every level
                adding up to the Level 1 figure. Tag them under Mapping to see them split properly.
              </p>
              <ul className="mt-2 space-y-1">
                {Object.entries(data.untagged).map(([key, amount]) => (
                  <li key={key} className="text-[11px] text-muted-foreground"
                      data-testid={`fp-explorer-untagged-${key}`}>
                    <span className="text-navy">{key.replace("_", " ")}</span>: {inr(amount)} untagged
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(
            <div className="panel p-5" data-testid="fp-explorer-live">
              <p className="label-caps">Where this month's figures come from</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {Object.entries(data.sources).map(([key, s]) => (
                  <span key={key} data-testid={`fp-explorer-source-${key}`}
                        className={`rounded-full px-3 py-1.5 text-[11px] ${
                          ["engine", "sheet"].includes(s.source)
                          ? "bg-success/15 text-success"
                          : "bg-warning/20 text-[hsl(30_88%_28%)]"}`}>
                    {key.replace("_", " ")}: {s.source === "engine"
                      ? "live · your uploaded books"
                      : s.source === "sheet"
                      ? `live · synced ${(s.synced_at || "").slice(0, 16).replace("T", " ")}`
                      : s.source === "demo"
                      ? "illustrative figures — demo engagement"
                      : "no data for this month"}
                  </span>
                ))}
              </div>
              {Object.entries(data.sources).filter(([, s]) => s.sheet_variance).length > 0 && (
                <div className="mt-3 rounded-xl bg-warning/10 p-3" data-testid="fp-explorer-sheet-variance">
                  <p className="label-caps text-[hsl(30_88%_28%)]">The connected sheets disagree with the books</p>
                  <ul className="mt-1.5 space-y-1">
                    {Object.entries(data.sources).filter(([, s]) => s.sheet_variance).map(([key, s]) => (
                      <li key={key} className="text-[11px] text-muted-foreground"
                          data-testid={`fp-explorer-sheet-variance-${key}`}>
                        <span className="text-navy">{key.replace("_", " ")}</span>: the sheet reports{" "}
                        {inr(s.sheet_total)}, {inr(Math.abs(s.sheet_variance))}{" "}
                        {s.sheet_variance < 0 ? "below" : "above"} the books. The books are used here.
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {data.reference && (
                <div className="mt-4 border-t border-border pt-3" data-testid="fp-explorer-reference">
                  <p className="label-caps">Sense-check against the approved output pack</p>
                  <div className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-4">
                    {[["revenue", "Revenue"], ["pvc", "Production VC"],
                      ["dvc", "Distribution VC"], ["fixed_cost", "Fixed cost"]].map(([k, l]) => {
                      const sheet = data.headline.find((h) => h.key === k)?.actual ?? 0;
                      const gap = Math.round((sheet - data.reference[k]) * 100) / 100;
                      return (
                        <div key={k} data-testid={`fp-explorer-ref-${k}`}>
                          <p className="text-[11px] text-muted-foreground">{l}</p>
                          <p className="font-mono text-xs text-navy">{inr(data.reference[k])}</p>
                          <p className={`text-[10px] ${Math.abs(gap) < 1
                            ? "text-success" : "text-[hsl(30_88%_28%)]"}`}>
                            {Math.abs(gap) < 1 ? "matches the sheet"
                              : `sheet differs by ${inr(Math.abs(gap))}`}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ---------- level 0: the P&L, clickable ---------- */}
          <div className="panel overflow-hidden" data-testid="fp-explorer-headline">
            <div className="border-b border-border px-6 py-4">
              <p className="label-caps">
                {monthLabel(period)} · {basis === "ytd" ? "cumulative" : "this month"}
              </p>
              <h3 className="font-display text-lg text-navy">
                Start here — click any block to open it up
              </h3>
            </div>
            <div className="space-y-4 p-6">
              {data.headline.slice(0, 1).map((h, i) => (
                <Placard key={h.key} h={h} i={i} hero
                         pair={data.reference?.control_detail?.[h.key]}
                         trend={data.trends?.totals?.[h.key]} periods={data.trends?.periods}
                         active={path[0] === h.key} onPick={() => setPath([h.key])} />
              ))}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
                {data.headline.slice(1).map((h, i) => (
                  <Placard key={h.key} h={h} i={i + 1}
                           pair={data.reference?.control_detail?.[h.key]}
                           trend={data.trends?.totals?.[h.key]} periods={data.trends?.periods}
                           active={path[0] === h.key} onPick={() => setPath([h.key])} />
                ))}
              </div>
              {data.trends?.periods?.length > 1 && (
                <p className="text-[10px] text-muted-foreground" data-testid="fp-explorer-trend-note">
                  Sparklines run over the last {data.trends.periods.length} months with figures
                  ({data.trends.periods.map(monthLabel).join(" · ")}) — hover one to read the
                  values; a red end point marks a jump of more than 20% against the month before.
                </p>
              )}
            </div>
          </div>

          {/* ---------- traded products margin (client's purchase register) ---------- */}
          {data.traded_analysis && (
            <div className="panel overflow-hidden" data-testid="fp-explorer-traded">
              <div className="border-b border-border px-6 py-4">
                <p className="label-caps">Traded products · {data.traded_analysis.filename}</p>
                <h3 className="font-display text-lg text-navy">
                  Margin {inr(data.traded_analysis.margin)} ({data.traded_analysis.margin_pct}%)
                  on {inr(data.traded_analysis.revenue)} of traded sales
                </h3>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  External purchases per the register; interentity purchases excluded. Traded goods
                  stay inside Net FG stock in the model — this panel is analysis only.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-px bg-border sm:grid-cols-4">
                {[
                  ["Opening stock", data.traded_analysis.opening_stock, "fp-traded-opening"],
                  ["+ Purchases (register)", data.traded_analysis.purchases, "fp-traded-purchases"],
                  ["− Closing stock", data.traded_analysis.closing_stock, "fp-traded-closing"],
                  ["= COGS", data.traded_analysis.cogs, "fp-traded-cogs"],
                ].map(([label, value, tid]) => (
                  <div key={tid} className="bg-card px-5 py-4" data-testid={tid}>
                    <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                      {label}
                    </p>
                    <p className="mt-1 font-mono text-sm font-semibold tabular-nums text-navy">
                      {inr(value)}
                    </p>
                  </div>
                ))}
              </div>
              {data.traded_analysis.suppliers?.length > 0 && (
                <div className="flex flex-wrap gap-2 px-6 py-4" data-testid="fp-traded-suppliers">
                  {data.traded_analysis.suppliers.map((s) => (
                    <span key={s.supplier}
                          className="rounded-full border border-border bg-muted/40 px-3 py-1 text-[11px] text-navy">
                      {s.supplier} · {inr(s.taxable_value)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}


          {/* ---------- the drill-down ---------- */}
          <AnimatePresence mode="wait">
            {node && (
              <motion.div key={path.join("/")} initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                          transition={{ duration: 0.22 }}
                          className="panel overflow-hidden" data-testid="fp-explorer-drill">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-6 py-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                      <button onClick={() => setPath([])} data-testid="fp-explorer-crumb-root"
                              className="flex items-center gap-1 text-muted-foreground hover:text-terracotta">
                        <ArrowLeft size={12} /> P&amp;L
                      </button>
                      {crumbs.map((crumb, i) => (
                        <Fragment key={crumb.id}>
                          <span className="text-muted-foreground">/</span>
                          <button onClick={() => setPath(path.slice(0, i + 1))}
                                  data-testid={`fp-explorer-crumb-${crumb.id}`}
                                  className={i === crumbs.length - 1
                                    ? "font-semibold text-navy"
                                    : "text-muted-foreground hover:text-terracotta"}>
                            {crumb.label}
                          </button>
                        </Fragment>
                      ))}
                    </div>
                    <h3 className="mt-1 font-display text-xl text-navy">{node.label}</h3>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <span className="font-mono text-sm text-navy">{inr(node.actual)}</span>
                      <Delta node={node} cost={cost} />
                      {data.trends?.nodes?.[node.id] && (
                        <Spark points={data.trends.nodes[node.id]} periods={data.trends.periods}
                               i={0} w={110} h={26} spikeLabel
                               testid="fp-explorer-spark-drill" />
                      )}
                      {rows.length > 0 && (
                        <span className="text-[11px] text-muted-foreground">
                          split by {node.child_dimension
                            || (path.length === 2 ? "category" : "line item")}
                          {" · "}{rows.length} item{rows.length === 1 ? "" : "s"}
                        </span>
                      )}
                    </div>
                  </div>
                  {rows.length > 0 && (
                    <div className="flex overflow-hidden rounded-full border border-border">
                      {[["share", "Share"], ["compare", "Budget vs actual"]].map(([k, l]) => (
                        <button key={k} onClick={() => setView(k)}
                                data-testid={`fp-explorer-view-${k}`}
                                className={`px-4 py-2 text-[10px] font-bold uppercase tracking-[0.12em] ${
                                  view === k ? "bg-navy text-white" : "bg-card text-navy hover:bg-muted"}`}>
                          {l}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {rows.length === 0 ? (
                  <div className="px-6 py-8" data-testid="fp-explorer-leaf">
                    <p className="label-caps">Deepest level</p>
                    <p className="mt-2 text-sm text-navy">
                      {node.label} is a single line item — {inr(node.actual)} against a budget of{" "}
                      {inr(node.budget)}
                      {node.variance_pct !== null && node.variance_pct !== undefined
                        ? `, ${Math.abs(node.variance_pct)}% ${node.variance > 0 ? "over" : "under"}`
                        : ""}.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="px-4 py-5">
                      {view === "share"
                        ? <ShareChart rows={rows} onPick={pick} />
                        : <CompareChart rows={rows} onPick={pick} />}
                    </div>
                    <button onClick={() => setShowNumbers(!showNumbers)}
                            data-testid="fp-explorer-numbers-toggle"
                            className="flex w-full items-center justify-between border-t border-border px-6 py-3 text-[10px] uppercase tracking-[0.14em] text-navy hover:bg-muted/30">
                      {showNumbers ? "Hide the numbers" : "Show the numbers"}
                      {showNumbers ? <CaretDown size={14} /> : <CaretRight size={14} />}
                    </button>
                    <AnimatePresence initial={false}>
                      {showNumbers && (
                        <motion.div initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.2 }} className="overflow-hidden">
                          <Numbers rows={rows} cost={cost} onPick={pick} />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* ---------- DVC pivoted: component first, outlets beneath ---------- */}
          {level === 3 && data.trees?.dvc_by_component && (
            <DvcByComponent tree={data.trees.dvc_by_component} trends={data.trends}
                            periods={data.trends?.periods} />
          )}

          {/* ---------- side-by-side lens (nothing splits at Level 1) ---------- */}
          {level > 1 && (
          <div className="panel overflow-hidden" data-testid="fp-explorer-lens">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-6 py-4">
              <div>
                <p className="label-caps">Side by side</p>
                <h3 className="font-display text-lg text-navy">
                  {(data.outlet_lens.revenue || []).length
                    ? `How the whole business splits across the ${(data.outlet_lens.revenue || []).length} blocks at this level`
                    : "Nothing splits at this level — switch to Level 2 or 3"}
                </h3>
              </div>
              <div className="flex flex-wrap overflow-hidden rounded-full border border-border">
                {[["revenue", "Revenue"], ["pvc", "Production VC"], ["dvc", "Distribution VC"],
                  ["fixed_cost", "Fixed cost"]].map(([k, l]) => (
                  <button key={k} onClick={() => setLens(k)} data-testid={`fp-explorer-lens-${k}`}
                          className={`px-4 py-2 text-[10px] font-bold uppercase tracking-[0.12em] ${
                            lens === k ? "bg-terracotta text-white" : "bg-card text-navy hover:bg-muted"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-2 p-4 lg:grid-cols-2">
              <ShareChart rows={data.outlet_lens[lens] || []}
                          onPick={(row) => row?.path && setPath(row.path)} />
              <CompareChart rows={data.outlet_lens[lens] || []}
                            onPick={(row) => row?.path && setPath(row.path)} />
            </div>
            <p className="flex items-center gap-2 border-t border-border px-6 py-3 text-[11px] text-muted-foreground">
              <ChartPie size={14} /> Click any block to open its own breakdown.
            </p>
          </div>
          )}

          <CostObjects tenantId={tenantId} period={period} />

          <Sources tenantId={tenantId} period={period} onSynced={load} />
        </div>
      )}
    </FpShell>
  );
}
